import { useAuthContext, useUploadFile } from '@core/shared';
import { AccountData, OptionsApi } from '@core/swagger-client';
import { Button, Delete, ErrorMessage, Upload, useFileDropzone } from '@core/ui';
import { useMutation } from '@tanstack/react-query';
import { MouseEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

export const EditBannerImage = ({ account }: { account: AccountData }) => {
    const { t } = useTranslation();
    const { upload, progress, isSuccess } = useUploadFile(`/api/account/banner-image`);
    const { refreshToken } = useAuthContext();
    const { getRootProps, getInputProps, isDragActive, rejectionCode } = useFileDropzone({
        multiple: false,
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length === 0) {
                return;
            }

            const filePayload = new FormData();
            filePayload.append('file', acceptedFiles[0]);
            upload(filePayload).then(() => {
                refreshToken();
            });
        },
        accept: {
            'image/png': ['.png'],
            'image/jpg': ['.jpg'],
            'image/webp': ['.webp']
        }
    });

    const deleteBannerImageMutation = useMutation(OptionsApi.account.deleteBannerImage());
    const deleteImage = (e: MouseEvent) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        deleteBannerImageMutation.mutate(undefined, { onSuccess: () => refreshToken() });
    };

    if (!account) {
        return <></>;
    }

    const assets = account.assets;
    return (
        <>
            <div
                {...getRootProps()}
                className={`group relative w-full min-h-32 ${isDragActive && 'bg-accent-lighter'} hover:bg-accent-lighter focus-visible:bg-accent-lighter rounded-lg flex mb-4 md:mb-6 outline-hidden overflow-hidden transition-colors duration-150 before:pt-[25%] before:float-left`}
            >
                {!assets.bannerImage && (
                    <div className="flex flex-col items-center m-auto p-2">
                        <Upload classNames="size-12 mb-1 stroke-current group-hover:stroke-primary transition-color duration-200 ease-in-out" />
                        <p className="font-accent font-w-heading text-3xl mb-2">{isDragActive ? 'Drop the files here ...' : 'Drag & drop'}</p>
                        <p>
                            <Trans components={{ span: <span className="border-b-2 border-primary" /> }} i18nKey="Generic.DragDrop.BrowseFiles" />
                        </p>
                        <p className="mt-4">
                            {t('Generic.DragDrop.AcceptedText')}
                            <span className="font-medium">{t('Generic.DragDrop.AcceptedImageTypes')}</span>
                        </p>
                    </div>
                )}
                {assets.bannerImage && <img src={assets.bannerImage.imageUrl} alt="" className="absolute inset-0 h-full object-cover" />}
                <div
                    className={`absolute inset-0 rounded-lg border-2 border-dashed ${!assets.bannerImage && !isDragActive && 'border-gray-500'} ${assets.bannerImage && !isDragActive && 'border-transparent'} ${isDragActive && 'border-accent'} group-hover:border-accent group-focus-visible:border-accent transition-colors duration-150`}
                ></div>
                {assets.bannerImage && (
                    <div className="relative flex gap-4 ms-auto mt-auto p-2">
                        <Button variant="primary-outline" aria-label={t('Account.Images.Button.UploadBannerImage.AriaLabel')}>
                            <Upload classNames="size-6 flex-none stroke-current menu:me-2" />
                            <span className="hidden menu:inline">{t('Generic.Button.UploadImage')}</span>
                        </Button>
                        <Button
                            onClick={deleteImage}
                            isLoading={deleteBannerImageMutation.isPending}
                            variant="warning-outline"
                            aria-label={t('Account.Images.Button.DeleteBannerImage.AriaLabel')}
                        >
                            <Delete classNames="size-6 flex-none stroke-current menu:me-2" />
                            <span className="hidden menu:inline">{t('Generic.Button.Delete')}</span>
                        </Button>
                    </div>
                )}
            </div>
            {rejectionCode && <ErrorMessage errorSubject="Generic.FileRejection." errorCode={rejectionCode} classNames="mt-4" />}
            <input {...getInputProps()} />
        </>
    );
};
